import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { useRouteMatch } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { SafeMarkdown } from 'src/lib/safeCompileMarkdown';
import { LinkableHeading } from 'src/lib/safeCompileMarkdown/SafeMarkdown';

import { GraphRef } from '../../hooks/useGraphRef';
import { useGraphVisibility } from '../../hooks/useGraphVisibility';
import { proposalRouteConfig } from '../../routes';
import { populateShortcodes } from '../shortcodes/populateShortcodes';
import { shortcodesConfig } from '../shortcodes/shortcodesConfig';

import { operationIdRegex } from './customComponentParsers';
import { CustomComponentsOrParagraph } from './CustomComponentsOrParagraph';

export const readmeMarkdownServiceFragment = gql`
  fragment ReadmeMarkdownServiceFragment on Service {
    id
    title
    variant(name: $graphVariant) {
      id
      url
    }
  }
`;

export const ReadmeMarkdown = ({
  className,
  content,
  graphRef,
  graphTitle,
  variantUrl,
  setHeadings,
}: {
  className?: string;
  content: string;
  graphRef: GraphRef;
  graphTitle: string | undefined;
  variantUrl: string | null | undefined;
  setHeadings?: React.Dispatch<React.SetStateAction<LinkableHeading[]>>;
}) => {
  const graphVisibilityType = useGraphVisibility();
  const isProposalRoute = !!useRouteMatch(proposalRouteConfig.definition);

  const collectionEntryMatches = [...content.matchAll(operationIdRegex)];
  const collectionEntryIds = collectionEntryMatches.flatMap(
    (collectionEntryMatch) => collectionEntryMatch?.groups?.uuid ?? [],
  );

  const { data: collectionEntryData, loading: collectionEntryDataLoading } =
    useQuery<
      GraphQLTypes.OperationCollectionEntriesReadmeQuery,
      GraphQLTypes.OperationCollectionEntriesReadmeQueryVariables
    >(
      gql`
        query OperationCollectionEntriesReadmeQuery(
          $collectionEntryIds: [ID!]!
        ) {
          operationCollectionEntries(collectionEntryIds: $collectionEntryIds) {
            id
            name
            currentOperationRevision {
              body
            }
            collection {
              id
              name
            }
          }
        }
      `,
      {
        variables: {
          collectionEntryIds,
        },
        skip: isProposalRoute,
      },
    );

  const { data: collectionsData, loading: collectionsDataLoading } = useQuery<
    GraphQLTypes.OperationCollectionCollectionsReadmeQuery,
    GraphQLTypes.OperationCollectionCollectionsReadmeQueryVariables
  >(
    gql`
      query OperationCollectionCollectionsReadmeQuery(
        $graphId: ID!
        $variant: String!
      ) {
        service(id: $graphId) {
          id
          variant(name: $variant) {
            id
            name
            operationCollections {
              id
              name
              operations {
                id
              }
              description
            }
          }
        }
      }
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        graphId: graphRef.graphId,
        variant: graphRef.graphVariant,
      },
      skip: isProposalRoute,
    },
  );
  const internalGraphLinking = useInternalGraphLinking();

  const markdownOptions = React.useMemo(
    () => ({
      wrapper: undefined,
      overrides: {
        p: {
          component: CustomComponentsOrParagraph,
          props: {
            collectionEntryData,
            collectionEntryDataLoading,
            collectionsData,
            collectionsDataLoading,
            className: 'mt-0 mb-3 text-base',
          } as Omit<
            React.ComponentProps<typeof CustomComponentsOrParagraph>,
            'children'
          >,
        },
      },
    }),
    [
      collectionEntryData,
      collectionEntryDataLoading,
      collectionsData,
      collectionsDataLoading,
    ],
  );

  const markdownWithTokensReplaced = React.useMemo(() => {
    return populateShortcodes(
      content,
      shortcodesConfig.reduce(
        (acc, { id, value }) => ({
          ...acc,
          [id]: value({
            graphTitle,
            graphRef,
            variantUrl,
            graphVisibilityType,
            internalGraphLinking,
          }),
        }),
        {} as Record<string, string>,
      ),
    );
  }, [
    content,
    graphRef,
    graphTitle,
    graphVisibilityType,
    internalGraphLinking,
    variantUrl,
  ]);

  return (
    <SafeMarkdown
      className={className}
      onHeadingsChange={setHeadings}
      markdown={markdownWithTokensReplaced}
      includeDefaultOverrides={true}
      shouldPopulateEmojis={true}
      analyticsLabelPrefix="README content"
      analyticsCategory="Variant Home"
      options={markdownOptions}
    />
  );
};
